import React from 'react';
import { X, Search, Calendar, Filter } from 'lucide-react';
import { useStore } from '../store';
import { translations } from '../types';
import { useEffect, useState } from 'react';

export function History() { 
  const { history, language } = useStore();
  const t = translations[language];
  const { deleteScan } = useStore();
  const [searchQuery, setSearchQuery] = React.useState('');
  const [dateFilter, setDateFilter] = React.useState('all');
  const [showFilters, setShowFilters] = React.useState(false);
  const [lastScanTimestamp, setLastScanTimestamp] = useState<number | null>(null);

  useEffect(() => {
    if (history.length > 0) {
      const latestScan = history[0];
      setLastScanTimestamp(latestScan.timestamp);
      
      // Remove highlight after 1 minute
      setTimeout(() => {
        setLastScanTimestamp(null);
      }, 60000);
    }
  }, [history]);

  const handleDelete = (timestamp: number) => {
    if (window.confirm(t.deleteConfirm)) {
      deleteScan(timestamp);
    }
  };

  const filteredHistory = React.useMemo(() => {
    return history.filter(scan => {
      // Apply search filter
      const matchesSearch = searchQuery === '' || 
        scan.tracking_numbers.toLowerCase().includes(searchQuery.toLowerCase());

      // Apply date filter
      const scanDate = new Date(scan.timestamp);
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      const isToday = scanDate.toDateString() === today.toDateString();
      const isYesterday = scanDate.toDateString() === yesterday.toDateString();
      
      const matchesDate = dateFilter === 'all' ||
        (dateFilter === 'today' && isToday) ||
        (dateFilter === 'yesterday' && isYesterday);

      return matchesSearch && matchesDate;
    });
  }, [history, searchQuery, dateFilter]);

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="mb-4">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="w-full flex items-center justify-between px-4 py-2 bg-white rounded-lg shadow-sm hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <span className="text-gray-700">{t.filter}</span>
          </div>
          <span className="text-sm text-gray-500">
            {filteredHistory.length} / {history.length}
          </span>
        </button>

        {showFilters && (
          <div className="mt-2 space-y-2 bg-white p-4 rounded-lg shadow-sm">
            <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
              <Search className="w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t.searchPlaceholder}
                className="w-full bg-transparent border-none focus:ring-0 text-sm"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-400" />
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="flex-1 bg-gray-50 border-none rounded-lg text-sm focus:ring-0"
              >
                <option value="all">{t.allDates}</option>
                <option value="today">{t.today}</option>
                <option value="yesterday">{t.yesterday}</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {history.length === 0 ? (
        <div className="app-card p-8 text-center">
          <p className="text-gray-500">{t.noHistory}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredHistory.map((scan, index) => (
            <div 
              key={`scan-${scan.timestamp}-${index}`} 
              className={`app-card p-4 relative group transition-all duration-500
                ${scan.isDuplicate ? 'border-l-4 border-yellow-500' : ''}
                ${lastScanTimestamp === scan.timestamp ? 'bg-green-50 animate-highlight' : ''}`}
            >
              <button
                onClick={() => handleDelete(scan.timestamp)}
                className="absolute right-2 top-2 p-2 text-red-600 hover:text-red-700 opacity-0 group-hover:opacity-100 transition-opacity"
                title={t.delete}
              >
                <X className="w-4 h-4" />
              </button>
              <div className="pr-8">
                <div className="font-mono text-sm break-all">{scan.tracking_numbers}</div>
                <div className="mt-2 flex justify-between text-sm text-gray-500">
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    scan.isDuplicate 
                      ? 'bg-yellow-100 text-yellow-800' 
                      : 'bg-gray-100'
                  }`}>
                    {scan.format}
                    {scan.isDuplicate && ` • ${t.duplicate}`}
                  </span>
                  <span>
                    {new Date(scan.timestamp).toLocaleString(
                      language === 'ru' ? 'ru-RU' : 'zh-CN'
                    )}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}